import {useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {getIsOperationPending, getOperationFailure} from "store/selectors/operation";
import {OperationType} from "types/operation";
import {initialize, login} from "./SignInPage.action";
import {
  paperAirplane,
  SignInPage as SignInPageUI,
  SignInPageFormData,
  TextType,
} from "@pinch-financial/pinch-ui-components";
import {yupResolver} from "@hookform/resolvers/yup";
import {useForm} from "react-hook-form";
import {getSchema} from "./SignInPage.schema";
import useDetectKeyboardOpen from "use-detect-keyboard-open";
import NavigationBar from "components/NavigationBar/NavigationBar";
import {useTranslation} from "react-i18next";
import usePageText from "~/hooks/usePageText";
import {getPageStatus} from "~/store/selectors/applicantMeta";

interface Props {
  containerRef?: React.RefObject<HTMLDivElement>;
}

const SignInPage = ({containerRef}: Props) => {
  const {t: fixedT} = useTranslation("", {keyPrefix: "signInPage"});
  const {getText, status: textStatus} = usePageText("SIGN_IN");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isSignInPending = useSelector(getIsOperationPending)(OperationType.signIn);
  const signInFailure = useSelector(getOperationFailure)(OperationType.signIn);
  const isKeyboardOpen = useDetectKeyboardOpen();
  const signInPageStatus = useSelector(getPageStatus)("SIGN_IN");

  let signInFailureMessage = "";

  if (signInFailure) {
    signInFailureMessage = fixedT("errorMessages.error");
  }

  const formHook = useForm<SignInPageFormData>({
    defaultValues: {
      email: "",
      password: "",
    },
    resolver: yupResolver(getSchema()),
  });

  const onSubmit = (values: SignInPageFormData) => {
    dispatch(login({...values, navigate}));
  };

  useEffect(() => {
    dispatch(initialize({navigate}));
  }, []);

  // Keep this so UI can update. React-hook-form problem.
  const {
    formState: {errors, touchedFields: touched},
  } = formHook;

  return (
    <SignInPageUI
      loading={
        textStatus === "not-started" ||
        textStatus === "pending" ||
        signInPageStatus === "submitting"
      }
      isMobileKeyboardOpen={isKeyboardOpen}
      containerRef={containerRef}
      header={getText(TextType.APP_PAGE_HEADER) ?? fixedT("header")!}
      subheader={getText(TextType.APP_PAGE_HEADER2) ?? fixedT("subheader")!}
      description={getText(TextType.APP_PAGE_BODY) ?? fixedT("description")}
      iconSrc={paperAirplane}
      iconAlt={fixedT("iconAlt")!}
      navigationBar={<NavigationBar />}
      nextButtonText={getText(TextType.APP_PAGE_NEXT_BUTTON) ?? fixedT("forwardButton")}
      emailLabel={fixedT("fields.email.label")}
      emailPlaceholder={fixedT("fields.email.placeholder")}
      passwordLabel={fixedT("fields.password.label")}
      passwordPlaceholder={fixedT("fields.password.placeholder")}
      forgetPasswordText={fixedT("forgetPassword")}
      signInFailureMessage={signInFailureMessage}
      invalidEmail={fixedT("messages.invalidEmail")}
      missingPassword={fixedT("messages.missingPassword")}
      isPending={isSignInPending}
      onNextPage={onSubmit}
    />
  );
};

export default SignInPage;
